import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Zap, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Mail, ExternalLink, Search } from 'lucide-react';

const relTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function DnoInbox() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [actionCount, setActionCount] = useState(0);
  const [query, setQuery] = useState('');
  const [onlyAction, setOnlyAction] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dno/inbox');
      setItems(r.data.items || []);
      setActionCount(r.data.action_required_count || 0);
    } catch {
      toast.error('Could not load the DNO inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (onlyAction) list = list.filter((i) => i.action_required);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        [i.customer_name, i.dno_reference, i.application_id, i.dno_name, i.summary, i.latest_email?.subject]
          .filter(Boolean).some((v) => v.toLowerCase().includes(q)));
    }
    return list;
  }, [items, query, onlyAction]);

  return (
    <Layout>
    <div className="space-y-6 max-w-5xl" data-testid="dno-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" /> DNO Inbox
          </h1>
          <p className="text-sm text-slate-500">Inbound DNO correspondence matched to your applications — anything still awaiting your reply is flagged.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="dno-inbox-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer, DNO ref, or request…"
            className="pl-9"
            data-testid="dno-inbox-search"
          />
        </div>
        <Button
          variant={onlyAction ? 'default' : 'outline'}
          size="sm"
          onClick={() => setOnlyAction((v) => !v)}
          className={onlyAction ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
          data-testid="dno-inbox-filter-action"
        >
          <AlertTriangle className="w-4 h-4 mr-1.5" /> Action required only ({actionCount})
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400" data-testid="dno-inbox-empty">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
          {items.length === 0 ? 'No DNO correspondence found in the mailboxes yet.' : 'Nothing matches your filters.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="dno-inbox-list">
          {filtered.map((it) => {
            const key = (it.application_id || it.customer_id) + it.latest_email.when;
            return (
              <Card key={key} className={it.action_required ? 'border-amber-300' : ''} data-testid={`dno-inbox-item-${it.dno_reference || it.customer_id}`}>
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {it.action_required ? (
                          <Badge className="bg-amber-600 text-white" data-testid="badge-action-required">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Action required
                          </Badge>
                        ) : it.replied ? (
                          <Badge variant="secondary" className="text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Replied
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Closed</Badge>
                        )}
                        <span className="font-semibold text-slate-900 dark:text-white">{it.customer_name || 'Unknown customer'}</span>
                        {it.dno_name && <span className="text-xs text-slate-400">· {it.dno_name}</span>}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Ref {it.dno_reference || it.application_id || '—'}
                        {it.status ? <> · {it.status}</> : null}
                      </p>
                    </div>
                    {it.customer_id && (
                      <Button asChild size="sm" variant="outline" data-testid={`dno-open-customer-${it.customer_id}`}>
                        <Link to={`/customers/${it.customer_id}`}>
                          Open customer <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                        </Link>
                      </Button>
                    )}
                  </div>

                  {it.summary && (
                    <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 px-3 py-2">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-0.5">What the DNO is asking for</p>
                      <p className="text-sm text-slate-700 dark:text-slate-200">{it.summary}</p>
                    </div>
                  )}

                  <div className="text-sm text-slate-600 dark:text-slate-300 flex items-start gap-2">
                    <Mail className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-100 truncate">{it.latest_email.subject || '(no subject)'}</p>
                      <p className="text-xs text-slate-500">
                        {it.latest_email.from_name || it.latest_email.from_address} · {relTime(it.latest_email.when)}
                        {it.inbound_count > 1 ? ` · ${it.inbound_count} emails` : ''}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{it.latest_email.preview}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </Layout>
  );
}
